import React, { useEffect, useRef, useState } from 'react';
import { siteData } from '../data/mock';
import { Check, MapPin } from 'lucide-react';

export const ThesisSection = () => {
  const { thesis } = siteData;
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="thesis"
      className="oa-section oa-grid-bg"
      style={{ borderTop: '1px solid var(--border-subtle)' }}
    >
      <div className="relative z-10" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Section Label */}
        <div
          className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ marginBottom: '24px' }}
        >
          <span className="oa-number">{thesis.number} // {thesis.label}</span>
        </div>

        {/* Headline */}
        <h2
          className={`oa-display-large transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ marginBottom: '24px', maxWidth: '900px' }}
        >
          {thesis.headline}
        </h2>

        {/* Body */}
        <p
          className={`oa-body-medium transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ marginBottom: '80px', maxWidth: '720px' }}
        >
          {thesis.body}
        </p>

        {/* Stat Blocks */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-0"
          style={{ marginBottom: '60px' }}
        >
          {thesis.statBlocks.map((block, i) => (
            <div
              key={i}
              className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{
                transitionDelay: `${300 + i * 100}ms`,
                padding: '48px 40px',
                borderRight: i === 0 ? '1px solid var(--border-subtle)' : 'none',
                background: 'rgba(255,255,255,0.02)',
              }}
            >
              <span
                className="oa-body-muted"
                style={{ fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase', display: 'block', marginBottom: '16px' }}
              >
                {block.region}
              </span>
              <span
                style={{
                  fontSize: '72px',
                  fontWeight: 700,
                  color: 'var(--brand-primary)',
                  fontFamily: "'Inter', sans-serif",
                  lineHeight: 1,
                  display: 'block',
                  marginBottom: '16px',
                }}
              >
                {block.stat}
              </span>
              <p className="oa-body-small" style={{ maxWidth: '400px' }}>
                {block.description}
              </p>
            </div>
          ))}
        </div>

        {/* Cards Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0" style={{ marginBottom: '60px' }}>
          {/* What We Look For */}
          <div
            className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{
              transitionDelay: '500ms',
              padding: '48px 40px',
              borderRight: '1px solid var(--border-subtle)',
              borderTop: '1px solid var(--border-subtle)',
              borderBottom: '1px solid var(--border-subtle)',
            }}
          >
            <h3 className="oa-heading-2" style={{ color: 'var(--text-primary)', marginBottom: '28px' }}>
              What We Look For
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {thesis.whatWeLookFor.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check size={16} style={{ color: 'var(--brand-primary)', marginTop: '4px', flexShrink: 0 }} />
                  <span className="oa-body-small" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Our Approach */}
          <div
            className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{
              transitionDelay: '600ms',
              padding: '48px 40px',
              borderTop: '1px solid var(--border-subtle)',
              borderBottom: '1px solid var(--border-subtle)',
            }}
          >
            <h3 className="oa-heading-2" style={{ color: 'var(--text-primary)', marginBottom: '28px' }}>
              Our Approach
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {thesis.ourApproach.map((para, i) => (
                <p key={i} className="oa-body-small" style={{ color: 'var(--text-secondary)' }}>
                  {para}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* Geography Tags */}
        <div
          className={`flex flex-wrap items-center gap-4 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: '700ms' }}
        >
          {thesis.geographies.map((geo, i) => (
            <div
              key={i}
              className="flex items-center gap-2"
              style={{
                padding: '8px 16px',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <MapPin size={12} style={{ color: 'var(--brand-primary)', opacity: 0.7 }} />
              <span
                style={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: 'var(--text-secondary)',
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                {geo}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
